/**
 * One entity, with its generated edit form.
 *
 * The heading follows the display convention the model itself carries: the
 * declared NAME is the label, and the short name sits beside it as a
 * monospace chip. Nothing here prettifies anything - if a part reads badly,
 * the fix belongs in the model, not in a lookup table on this side.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { FROM_KEY, useResultNav } from '../components/entity/resultNav'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ElementComments from '../components/common/ElementComments'
import EmptyState from '../components/common/EmptyState'
import EntityForm from '../components/entity/EntityForm'
import LinkEditor from '../components/entity/LinkEditor'
import DeleteElementDialog from '../components/tree/DeleteElementDialog'
import { ApiError, api } from '../api'
import { findKind } from '../schema'
import RunMatrix from '../components/verification/RunMatrix'
import VerificationIncludes from '../components/verification/VerificationIncludes'
import VerificationReportMenu from '../components/verification/VerificationReportMenu'
import LinkedRequirements from '../components/links/LinkedRequirements'
import { LINKABLE_KINDS } from '../components/links/linkable'
import type { EntityRow } from '../schema'
import OwnAttributes from '../components/requirements/OwnAttributes'
import { useCanWrite, usePrincipal, useRefreshAppData, useSchema } from '../state/AppData'
import { useProject } from '../state/Project'
import { useToasts } from '../state/Toasts'
import { MONOSPACE } from '../theme'

/**
 * The identifiers a reference-list field answers with.
 *
 * Defensive because the payload is generated from the registry: a kind that
 * states no links answers with the key missing rather than with an empty list.
 */
function asIdentifiers(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string')
}


export default function EntityDetail() {
  const { plural = '', id = '' } = useParams()
  const [query] = useSearchParams()
  const listFrom = query.get(FROM_KEY) ?? ''
  const schema = useSchema()
  const refreshSchema = useRefreshAppData()
  const navigate = useNavigate()
  const toasts = useToasts()
  const { allowed, reason } = useCanWrite()
  const principal = usePrincipal()
  const { current, branch } = useProject()

  const [row, setRow] = useState<EntityRow | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [confirming, setConfirming] = useState(false)

  const kind = findKind(schema, plural)

  const load = useCallback(() => {
    if (!kind) return
    setError(null)
    // Drop the previous entity before asking for the next one. ':plural/:id' is
    // one route, so this component stays mounted when the target changes, and a
    // row of one kind would otherwise render inside another kind's generated
    // form until the fetch returned - showing a port's fields as a state's.
    setRow(null)
    api
      .get(kind.plural, id)
      .then(setRow)
      .catch((failure: ApiError) => setError(failure))
  }, [kind?.plural, id])

  useEffect(() => {
    load()
  }, [load])

  // Stepping through the list this was opened from (#109). The data router's
  // blocker (state/Unsaved) guards these navigations exactly as it guards a
  // link, so unsaved edits prompt rather than vanish.
  const steps = useResultNav(plural, id)
  const step = useCallback(
    (to: string | null) => {
      if (to) navigate(to, { state: steps?.state })
    },
    [navigate, steps?.state],
  )
  useEffect(() => {
    if (!steps) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      const target = event.target as HTMLElement | null
      // Alt+arrow is word/caret movement or browser history inside a field.
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      event.preventDefault()
      step(event.key === 'ArrowLeft' ? steps.previous : steps.next)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [steps, step])

  if (!kind) {
    return (
      <EmptyState
        title={`No such thing as '${plural}'`}
        actions={[{ label: 'Go to the dashboard', onClick: () => navigate('/') }]}
      />
    )
  }

  if (error) {
    return (
      <EmptyState
        title={error.status === 404 ? `No ${kind.label.toLowerCase()} ${id}` : 'Could not load'}
        description={error.message}
        actions={[
          { label: `Back to ${kind.plural}`, onClick: () => navigate(`/${kind.plural}`) },
          { label: 'Try again', onClick: load, variant: 'outlined' },
        ]}
      />
    )
  }

  if (!row) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Breadcrumbs>
        <RouterLink to={listFrom ? `/${kind.plural}?${listFrom}` : `/${kind.plural}`}>{kind.label}s</RouterLink>
        <Typography color="text.primary" sx={{ fontFamily: MONOSPACE }}>
          {row.id}
        </Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h5">{row.name || row.id}</Typography>
            <Chip label={row.id} size="small" sx={{ fontFamily: MONOSPACE }} />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: MONOSPACE, wordBreak: 'break-all' }}
          >
            {row.qualified_name}
          </Typography>
        </Box>

        {steps && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              startIcon={<ChevronLeftIcon />}
              disabled={!steps.previous}
              onClick={() => step(steps.previous)}
              title="Previous result (Alt+←)"
            >
              Prev
            </Button>
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {steps.index + 1} of {steps.total}
            </Typography>
            <Button
              size="small"
              endIcon={<ChevronRightIcon />}
              disabled={!steps.next}
              onClick={() => step(steps.next)}
              title="Next result (Alt+→)"
            >
              Next
            </Button>
          </Stack>
        )}

        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirming(true)}
              disabled={!allowed}
            >
              Delete
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!row.element_id && row.element_id !== undefined && (
        <Alert severity="warning">
          This row has no stored syntax node, so it cannot be edited. Re-run{' '}
          <code>best db ingest</code>.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <EntityForm kind={kind} row={row} onSaved={setRow} />
      </Paper>

      {/*
        Requirements only, and outside the form on purpose: a source and a
        cross reference are CONNECTIONS rather than fields, so each is its own
        write to its own route. The form shows them read-only; this edits them.
      */}
      {kind.name === 'requirement' && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={3}>
            <OwnAttributes
              identifier={String(row.id)}
              names={asIdentifiers(row.own_attributes)}
              allowed={allowed}
              reason={reason}
              // The SCHEMA first: a declared attribute is a new field, and the
              // form is generated from the schema read at app start.
              onChanged={() => void refreshSchema().then(load)}
            />
            <LinkEditor
              schema={schema}
              label="Sources"
              help="Why this requirement exists. No source is primary — a requirement may derive from several, and it appears under each of them in the flowdown."
              target="requirement"
              value={asIdentifiers(row.sources)}
              allowed={allowed}
              reason={reason}
              onAdd={(identifier) => api.addSource(row.id, identifier)}
              onRemove={(identifier) => api.removeSource(row.id, identifier)}
              onChanged={load}
            />
            <LinkEditor
              schema={schema}
              label="Related"
              help="Something similar worth checking. Belongs to neither requirement, so it is written at package level and shows on both."
              target="requirement"
              value={asIdentifiers(row.related)}
              allowed={allowed}
              reason={reason}
              onAdd={(identifier) => api.addRelated(row.id, identifier)}
              onRemove={(identifier) => api.removeRelated(row.id, identifier)}
              onChanged={load}
            />
          </Stack>
        </Paper>
      )}

      {kind.name === 'verification' && row.verification_kind === 'plan' && Boolean(row.is_definition) && (
        // The plan's cases as a table (issue #105). Outside the form, as the
        // requirement's links are: a member is a declaration of its own, not a
        // field of the plan.
        <Paper sx={{ p: 2 }}>
          <RunMatrix
            plan={{ id: String(row.id), name: String(row.name ?? ''), qualified_name: String(row.qualified_name) }}
          />
        </Paper>
      )}

      {kind.name === 'verification' &&
        (row.verification_kind === 'plan' || row.verification_kind === 'campaign') &&
        Boolean(row.is_definition) && (
          // Existing defs this plan or campaign includes as usages (issue #113).
          <Paper sx={{ p: 2 }}>
            {/* The whole tree below this def as a file (issue #119). */}
            <VerificationReportMenu identifier={String(row.id)} />
            <VerificationIncludes
              owner={{
                id: String(row.id),
                qualified_name: String(row.qualified_name),
                kind: row.verification_kind as 'plan' | 'campaign',
              }}
            />
          </Paper>
        )}

      {LINKABLE_KINDS.includes(kind.name) && (
        // What links HERE from the requirements side (issue #48): a state, an
        // interface, a port, a part or a function is where a requirement's
        // link lands, and the link is stored on the requirement.
        <Paper sx={{ p: 2 }}>
          <LinkedRequirements kind={kind.name} identifier={row.id} />
        </Paper>
      )}

      {/*
        Issue #99, phase C. Reading the merge request needs a GitLab token, so
        nobody anonymous is asked (the client reads any 401 as an expired
        session), and a single-database run has no repository behind it. The
        anchor lets the change list's comment button open it directly.
      */}
      {current && !principal.anonymous && (
        <ElementComments
          key={`${kind.plural}/${row.id}`}
          slug={current.slug}
          branch={branch}
          element={row.id}
          canComment={allowed}
          reason={reason}
        />
      )}

      {confirming && (
        <DeleteElementDialog
          row={{ plural: kind.plural, id: row.id, name: row.name || row.id }}
          onClose={() => setConfirming(false)}
          onDeleted={(message) => {
            toasts.succeeded(message)
            navigate(`/${kind.plural}`)
          }}
        />
      )}
    </Stack>
  )
}
